/**
 * InspirationPage.jsx - Homepage Ispirazione/Journal
 * ===================================================
 * 100% CMS-driven from blueprint_inspiration_config, 
 * blueprint_posts, blueprint_post_categories
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Search, ArrowRight, Clock, Calendar, BookOpen, Layers, 
  Compass, Building2, Leaf, ChevronRight, Loader2, Bookmark,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Icon mapping for categories
const iconMap = {
  BookOpen, Layers, Compass, Building2, Leaf
};

const InspirationPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Load CMS config and data
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load config
        const { data: configData } = await supabase
          .from('blueprint_inspiration_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        
        if (configData) setConfig(configData);

        // Load categories
        const { data: catData } = await supabase
          .from('blueprint_post_categories')
          .select('*')
          .eq('is_active', true)
          .eq('show_in_homepage', true)
          .order('order_index');
        
        if (catData) setCategories(catData);

        // Load featured posts with category and author
        const { data: postsData } = await supabase
          .from('blueprint_posts')
          .select(`
            *,
            category:blueprint_post_categories(slug, name_it, color),
            author:blueprint_authors(name, avatar_url, role)
          `)
          .eq('status', 'published')
          .eq('is_featured', true)
          .order('featured_order')
          .limit(5);
        
        if (postsData) setFeaturedPosts(postsData);
      } catch (err) {
        console.error('Error loading inspiration data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/inspiration/articles?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Text helper
  const t = (key, fallback) => config?.[key] || fallback;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Get category icon
  const getCategoryIcon = (iconName) => {
    const Icon = iconMap[iconName] || Layers;
    return <Icon className="w-6 h-6" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#B08B5C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-16">
        {/* Hero Section */}
        <section className="relative min-h-[45vh] flex items-center">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/90 to-transparent z-10" />
            <img 
              src={config?.hero_image_url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80"}
              alt="Hero"
              className="w-full h-full object-cover opacity-70"
            />
          </div>
          
          <div className="relative z-20 max-w-[1800px] mx-auto px-6 md:px-12 py-16 w-full">
            <div className="max-w-2xl">
              {/* Label */}
              <p className="text-[#B08B5C] text-sm font-semibold tracking-wider mb-4">
                {t('hero_label', 'ISPIRAZIONE')}
              </p>
              
              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4 leading-tight">
                {t('hero_title', 'Storie, materiali, ispirazioni.')}
              </h1>
              
              {/* Subtitle */}
              <h2 className="text-2xl md:text-3xl font-serif text-white/80 mb-6">
                {t('hero_subtitle', 'Conoscere la pietra per progettare il futuro.')}
              </h2>
              
              {/* Description */}
              <p className="text-white/60 text-lg mb-8 leading-relaxed">
                {t('hero_description', 'Approfondimenti, guide e progetti dal mondo dell\'architettura e del design. Un punto di riferimento per chi lavora con i materiali naturali.')}
              </p>
              
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="flex gap-2 max-w-lg">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('search_placeholder', 'Cerca articoli, materiali, guide...')}
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
                  />
                </div>
                <Button type="button" variant="outline" className="px-4 border-white/10 text-white/60 hover:text-white hover:bg-white/5">
                  <SlidersHorizontal className="w-5 h-5" />
                </Button>
              </form>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="max-w-[1800px] mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/inspiration/articles?category=${cat.slug}`}
                className="group bg-[#121212] rounded-xl p-5 border border-[#1a1a1a] hover:border-[#B08B5C]/30 transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-[#B08B5C]/10 flex items-center justify-center text-[#B08B5C] mb-4 group-hover:bg-[#B08B5C]/20 transition-colors">
                  {getCategoryIcon(cat.icon)}
                </div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-1">
                  {cat.name_it || cat.name}
                </h3>
                <p className="text-white/40 text-xs leading-relaxed">
                  {cat.description_it || cat.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Posts Section */}
        <section className="max-w-[1800px] mx-auto px-6 md:px-12 py-12">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-serif text-white">
              {t('featured_title', 'In evidenza')}
            </h2>
            <Link 
              to="/inspiration/articles"
              className="text-[#B08B5C] text-sm flex items-center gap-1 hover:underline"
            >
              {t('featured_cta', 'Scopri tutti gli articoli')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {/* Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {featuredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/inspiration/${post.slug}`}
                className="group"
              >
                {/* Image */}
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#121212] mb-4 relative">
                  {post.cover_image_url && (
                    <img 
                      src={post.cover_image_url} 
                      alt={post.title_it || post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                  {/* Category Badge */}
                  {post.category && (
                    <span 
                      className="absolute top-3 left-3 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full"
                      style={{ 
                        backgroundColor: post.category.color || '#B08B5C',
                        color: '#0a0a0a'
                      }}
                    >
                      {post.category.name_it?.split(' ')[0] || post.category.slug}
                    </span>
                  )}
                  {/* Bookmark */}
                  <button className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 flex items-center justify-center text-white/60 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Bookmark className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Content */}
                <h3 className="text-white font-medium mb-2 line-clamp-2 group-hover:text-[#B08B5C] transition-colors">
                  {post.title_it || post.title}
                </h3>
                <p className="text-white/40 text-sm mb-3 line-clamp-2">
                  {post.excerpt_it || post.excerpt}
                </p>
                
                {/* Meta */}
                <div className="flex items-center gap-3 text-white/30 text-xs">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.reading_time} {t('reading_time_suffix', 'min di lettura')}
                  </span>
                  <span>•</span>
                  <span>{formatDate(post.published_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default InspirationPage;
